"use client";
import Link from "next/link";
import { Printer, ShieldCheck, ArrowLeft, BadgeCheck } from "lucide-react";
import { DataState } from "@/components/ui";
import { useApi } from "@/lib/hooks/use-api";
import { trackName, TRACKS } from "@/lib/range/tracks.ts";
import type { CertificateDto } from "@/lib/local/certificates.ts";

/** Название дорожки по id (сквозной поиск по всем направлениям). */
function trackLabel(track: string): string {
  if (!track || track === "core") return "Базовый курс";
  for (const dir of Object.keys(TRACKS)) {
    const found = TRACKS[dir].find((t) => t.id === track);
    if (found) return found.name;
  }
  return track;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function CertificatePage({ id }: { id: string }) {
  const request = useApi<CertificateDto>(`/certificates/${encodeURIComponent(id)}`);
  const cert = request.data;
  const verifyUrl = typeof window !== "undefined"
    ? `${window.location.origin}/certificate/${id}`
    : `/certificate/${id}`;

  return (
    <DataState {...request} retry={request.reload}>
      {cert && (
        <div className="cert-wrap">
          <div className="cert-toolbar no-print">
            <Link href="/dashboard" className="button ghost">
              <ArrowLeft size={15} />
              На главную
            </Link>
            <button className="button primary" onClick={() => window.print()}>
              <Printer size={16} />
              Печать / PDF
            </button>
          </div>

          <div className="cert-sheet" id="cert-sheet">
            <div className="cert-border" aria-hidden="true" />
            <div className="cert-head">
              <div className="cert-brand">
                <span className="cert-logo">
                  <ShieldCheck size={20} />
                </span>
                <div>
                  <strong>CyberAlem</strong>
                  <small>Платформа кибербезопасности</small>
                </div>
              </div>
              <div className="cert-idbox">
                <small>№ сертификата</small>
                <strong>{cert.id}</strong>
              </div>
            </div>

            <div className="cert-body">
              <span className="cert-kicker">
                {cert.grade === "excellent" ? "С отличием" : "Аттестация пройдена"}
              </span>
              <h1 className="cert-title">СВИДЕТЕЛЬСТВО</h1>
              <p className="cert-lead">
                о прохождении практической подготовки
              </p>
              <p className="cert-name">{cert.user_name}</p>
              <p className="cert-prog">
                успешно выполнил(а) практический сценарий
              </p>
              <h2 className="cert-scenario">{cert.scenario_title}</h2>
              <div className="cert-trackchip">
                Дорожка: {trackLabel(cert.track)}
              </div>

              <div className="cert-metrics">
                <div className="cert-metric">
                  <small>Результат</small>
                  <strong>
                    {cert.score}
                    <span>/100</span>
                  </strong>
                </div>
                <div className="cert-metric">
                  <small>Статус</small>
                  <strong>
                    <BadgeCheck size={15} />
                    {cert.status === "COMPLETED" ? "COMPLETED" : "PASSED"}
                  </strong>
                </div>
                <div className="cert-metric">
                  <small>Дата</small>
                  <strong>{fmtDate(cert.finished_at)}</strong>
                </div>
              </div>
            </div>

            <div className="cert-foot">
              <div className="cert-verify">
                <small>Проверка подлинности</small>
                <code>{verifyUrl}</code>
              </div>
              <div className="cert-sign">
                <small>КиберAlem · {new Date(cert.issued_at).getFullYear()}</small>
              </div>
            </div>
          </div>
        </div>
      )}
    </DataState>
  );
}
